import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Radio, InputNumber,
  message, Space, Tag, Switch, Popconfirm, Card, Statistic, Row, Col
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  BookOutlined, FileTextOutlined
} from '@ant-design/icons';
import { topicAPI } from '../services/api';
import { EssayTopic, EssayTypes } from '../types';
import {
  buildFilters,
  compareDate,
  compareNumber,
  compareText,
  createValueFilter,
} from '../utils/tableHelpers';

const { TextArea } = Input;

const TopicManagement: React.FC = () => {
  const [topics, setTopics] = useState<EssayTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState<EssayTopic | null>(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    continuation: 0,
    application: 0,
  });
  const [form] = Form.useForm();

  // 加载题目列表
  const loadTopics = async () => {
    setLoading(true);
    try {
      const response = await topicAPI.list({ page_size: 100 });
      const topicList = response.data.data || [];
      setTopics(topicList);

      // 计算统计信息
      setStatistics({
        total: topicList.length,
        active: topicList.filter((t: EssayTopic) => t.is_active).length,
        continuation: topicList.filter((t: EssayTopic) => t.essay_type === EssayTypes.CONTINUATION).length,
        application: topicList.filter((t: EssayTopic) => t.essay_type === EssayTypes.APPLICATION).length,
      });
    } catch (error) {
      message.error('加载题目失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopics();
  }, []);

  // 创建或更新题目
  const handleSubmit = async (values: any) => {
    try {
      if (editingTopic) {
        await topicAPI.update(editingTopic.id, values);
        message.success('题目更新成功');
      } else {
        await topicAPI.create(values);
        message.success('题目创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTopic(null);
      loadTopics();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  // 删除题目
  const handleDelete = async (id: number) => {
    try {
      await topicAPI.delete(id);
      message.success('题目删除成功');
      loadTopics();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  // 切换启用状态
  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await topicAPI.update(id, { is_active: !isActive });
      message.success(isActive ? '题目已停用' : '题目已启用');
      loadTopics();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 打开创建/编辑模态框
  const openModal = (topic?: EssayTopic) => {
    if (topic) {
      setEditingTopic(topic);
      form.setFieldsValue(topic);
    } else {
      setEditingTopic(null);
      form.resetFields();
      form.setFieldsValue({
        essay_type: EssayTypes.CONTINUATION,
        word_limit: 150,
      });
    }
    setModalVisible(true);
  };

  const columns = [
    {
      title: '题目标题',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: compareText((record: EssayTopic) => record.title),
      filters: buildFilters(topics, (record: EssayTopic) => record.title),
      onFilter: createValueFilter((record: EssayTopic) => record.title),
      filterSearch: true,
      render: (text: string, record: EssayTopic) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{text}</span>
          {!record.is_active && <Tag color="red">已停用</Tag>}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'essay_type',
      key: 'essay_type',
      width: '10%',
      sorter: compareText((record: EssayTopic) => record.essay_type),
      render: (type: string) => (
        <Tag color={type === EssayTypes.APPLICATION ? 'blue' : 'green'}>
          {type === EssayTypes.APPLICATION ? '应用文' : '读后续写'}
        </Tag>
      ),
      filters: [
        { text: '读后续写', value: EssayTypes.CONTINUATION },
        { text: '应用文', value: EssayTypes.APPLICATION },
      ],
      onFilter: (value: any, record: EssayTopic) => record.essay_type === value,
    },
    {
      title: '字数要求',
      dataIndex: 'word_limit',
      key: 'word_limit',
      width: '10%',
      sorter: compareNumber((record: EssayTopic) => record.word_limit),
      render: (limit: number) => `${limit}词`,
    },
    {
      title: '话题',
      dataIndex: 'topic',
      key: 'topic',
      width: '15%',
      ellipsis: true,
      sorter: compareText((record: EssayTopic) => record.topic),
      filters: buildFilters(topics, (record: EssayTopic) => record.topic, {
        includeEmpty: true,
        emptyText: '未设置话题',
      }),
      onFilter: createValueFilter((record: EssayTopic) => record.topic),
      filterSearch: true,
      render: (text: string) => text || '-',
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: '10%',
      sorter: (a: EssayTopic, b: EssayTopic) => a.usage_count - b.usage_count,
      filters: [
        { text: '未使用', value: 'unused' },
        { text: '已使用', value: 'used' },
      ],
      onFilter: (value: boolean | string | number | bigint, record: EssayTopic) => (
        value === 'used' ? record.usage_count > 0 : record.usage_count === 0
      ),
      render: (count: number) => (
        <Tag color={count > 10 ? 'gold' : count > 0 ? 'cyan' : 'default'}>
          {count}次
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      sorter: compareDate((record: EssayTopic) => record.created_at),
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_: any, record: EssayTopic) => (
        <Space size="small">
          <Switch
            checked={record.is_active}
            onChange={() => handleToggleActive(record.id, record.is_active)}
            checkedChildren="启用"
            unCheckedChildren="停用"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个题目吗？"
            description={record.usage_count > 0 ? "该题目已被使用，删除后将只是停用" : undefined}
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="题目总数"
              value={statistics.total}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="启用中"
              value={statistics.active}
              valueStyle={{ color: '#3f8600' }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="读后续写"
              value={statistics.continuation}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="应用文"
              value={statistics.application}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="题目管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            新建题目
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={topics}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个题目`,
          }}
        />
      </Card>

      <Modal
        title={editingTopic ? '编辑题目' : '创建新题目'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingTopic(null);
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="题目标题"
            rules={[{ required: true, message: '请输入题目标题' }]}
          >
            <Input placeholder="如：A Run to Remember" />
          </Form.Item>

          <Form.Item
            name="essay_type"
            label="作文类型"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={EssayTypes.CONTINUATION}>读后续写</Radio>
              <Radio value={EssayTypes.APPLICATION}>应用文写作</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="word_limit"
            label="字数要求"
            rules={[{ required: true, message: '请输入字数要求' }]}
          >
            <InputNumber
              min={50}
              max={300}
              step={10}
              addonAfter="词"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="topic"
            label="话题/主题（可选）"
          >
            <Input placeholder="如：友谊、冒险、成长等" />
          </Form.Item>

          <Form.Item
            name="requirements"
            label="详细要求"
            extra="可以包含续写段落开头、应用文格式要求等"
          >
            <TextArea
              rows={8}
              placeholder="请输入作文的详细要求..."
            />
          </Form.Item>

          {editingTopic && (
            <Form.Item
              name="is_active"
              label="状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TopicManagement;
